import { useTranslate } from '@refinedev/core';
import { TextField, DateField } from '@refinedev/antd';
import { Typography, Tag, Divider, Table } from 'antd';
import type {
  ContentManifestOut,
  ContentManifestFileOut,
} from '@/api/generated/schemas';
import { formatFileSize } from '@/utils/fileSize';
import { ProductTranscodingJobsSection } from './ProductTranscodingJobsSection';

const { Title } = Typography;

export const ProductManifestTab = ({
  manifest,
  productId,
}: {
  manifest: ContentManifestOut | null | undefined;
  productId: number;
}) => {
  const t = useTranslate();

  if (!manifest) {
    return (
      <Typography.Text type="secondary">
        {t('content_catalog.products.manifest.not_available')}
      </Typography.Text>
    );
  }

  return (
    <>
      <Title level={4}>{t('content_catalog.products.tabs.manifest')}</Title>

      <Title level={5}>{t('base.fields.id')}</Title>
      <TextField value={manifest.id} />

      {manifest.created_at && (
        <>
          <Title level={5}>{t('base.fields.created_at')}</Title>
          <DateField value={manifest.created_at} format="LLL" />
        </>
      )}

      {manifest.files && manifest.files.length > 0 && (
        <>
          <Divider />
          <Title level={4}>
            {t('content_catalog.products.manifest.files_title')}
          </Title>
          <Table<ContentManifestFileOut>
            dataSource={manifest.files}
            rowKey="id"
            pagination={false}
            size="small"
            expandable={{
              expandedRowRender: (record) => (
                <pre
                  style={{
                    fontSize: 12,
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {JSON.stringify(record, null, 2)}
                </pre>
              ),
            }}
          >
            <Table.Column dataIndex="id" title={t('base.fields.id')} />
            <Table.Column
              dataIndex="external_id"
              title={t('content_catalog.products.manifest.fields.external_id')}
            />
            <Table.Column
              dataIndex="sequence_number"
              title={t(
                'content_catalog.products.manifest.fields.sequence_number'
              )}
            />
            <Table.Column
              dataIndex="file_format"
              title={t('content_catalog.products.manifest.fields.file_format')}
              render={(value: string) => (value ? <Tag>{value}</Tag> : '-')}
            />
            <Table.Column
              dataIndex="file_size_bytes"
              title={t('content_catalog.products.manifest.fields.file_size')}
              render={(value: number) =>
                value != null ? formatFileSize(value) : '-'
              }
            />
            <Table.Column
              dataIndex="s3_reference"
              title={t('content_catalog.products.manifest.fields.s3_status')}
              render={(value: unknown) => (
                <Tag color={value ? 'green' : 'error'}>
                  {value ? t('common.yes') : t('common.no')}
                </Tag>
              )}
            />
          </Table>
        </>
      )}

      <ProductTranscodingJobsSection productId={productId} />
    </>
  );
};
